import { route, number, string } from 'react-router-typesafe-routes/dom'
import { dateString, monthString } from '@/modules/shared/routes/codecs'

/**
 * Typed counterparts of `driversRoutesMap` / `driverRoutesConfig`.
 *
 *   - `useTypedParams(DRIVER_ROUTES.violations)` returns
 *     `{ companyId: number, driverId: number, from: string, to: string }`.
 *   - `DRIVER_ROUTES.violations.buildPath({ companyId: 77, driverId: 1,
 *     from: '2026-01-01', to: '2026-01-31' })` is compile-time checked.
 *
 * Library quirk: path templates MUST NOT start or end with `/`. `.path`
 * returns the absolute form for `<Route path={...}/>`.
 */

const base = 'et/company/:companyId/driverModule' as const

// Reused shapes — extract to keep each entry compact.
const companyOnly = { companyId: string().defined() }
const companyAndDriver = {
  companyId: string().defined(),
  driverId: number().defined(),
}
const companyAndDateRange = {
  companyId: string().defined(),
  from: dateString.defined(),
  to: dateString.defined(),
}
const singleWithDateRange = {
  companyId: string().defined(),
  driverId: number().defined(),
  from: dateString.defined(),
  to: dateString.defined(),
}

export const DRIVER_ROUTES = {
  // --- Single (per-driver) ---
  contracts: route(`${base}/basicData/driver/:driverId`, {
    params: companyAndDriver,
  }),
  activities: route(`${base}/activities/driver/:driverId`, {
    params: companyAndDriver,
  }),
  chartDetails: route(`${base}/activities/driver/:driverId/chartDetails/:year`, {
    params: {
      ...companyAndDriver,
      year: number().defined(),
    },
  }),
  violations: route(`${base}/driver/:driverId/violation/from/:from/to/:to`, {
    params: singleWithDateRange,
  }),
  violationsNew: route(
    `${base}/driver/:driverId/violation-new/from/:from/to/:to`,
    { params: singleWithDateRange },
  ),
  businessTrips: route(
    `${base}/driver/:driverId/businessTrip/from/:from/to/:to`,
    { params: singleWithDateRange },
  ),
  businessTripNew: route(
    `${base}/driver/:driverId/newGroupedBusinessTrip/from/:from/to/:to`,
    { params: singleWithDateRange },
  ),
  businessTripEdit: route(
    `${base}/driver/:driverId/editBusinessTrip/:id`,
    {
      params: {
        ...companyAndDriver,
        id: number().defined(),
      },
    },
  ),
  timesheet: route(`${base}/timesheet/driver/:driverId`, {
    params: companyAndDriver,
  }),
  schedule: route(`${base}/driver/:driverId/schedule/year/:year`, {
    params: {
      ...companyAndDriver,
      year: number().defined(),
    },
  }),
  foreignAmountsPreview: route(
    `${base}/driver/:driverId/foreignAmounts/month/:yearMonth`,
    {
      params: {
        ...companyAndDriver,
        yearMonth: monthString.defined(),
      },
    },
  ),

  // --- Group-level (across drivers) ---
  groupContracts: route(`${base}/basicData`, { params: companyOnly }),
  groupViolations: route(`${base}/violations`, { params: companyOnly }),
  groupViolationsNew: route(
    `${base}/violations-new/from/:from/to/:to`,
    { params: companyAndDateRange },
  ),
  groupBusinessTrips: route(
    `${base}/business-trips/from/:from/to/:to`,
    { params: companyAndDateRange },
  ),
  groupChartDetails: route(`${base}/chartDetails/from/:from/to/:to`, {
    params: companyAndDateRange,
  }),
  groupTimesheet: route(`${base}/timesheet`, { params: companyOnly }),
  groupTimesheetBonusAndRewards: route(`${base}/bonusAndRewards`, {
    params: companyOnly,
  }),
}
